import { Component, OnInit, HostBinding, inject } from '@angular/core';
import { PageComponent, sleep, OpenGraph, Cache, AppConfig } from '@tytapp/common';
import { UserService } from '@tytapp/user';
import {
    ApiCarouselSlide, ApiTopic, ApiFollowedShow, ApiHomePage,
    ApiPoll, ApiUser, ApiArticle, ApiHomeRow, ApiAnnouncement,
    ApiLiveStream, ApiLiveSchedule
} from '@tytapp/api';
import { PageApiService } from '@tytapp/common-ui';
import { HostApi } from '@tytapp/host-api';
import { isClientSide, isServerSide } from '@tytapp/environment-utils';
import { BillingService } from '@tytapp/app/billing.service';
import { PetitionHomeCard, PollHomeCard } from '@tytapp/app/pages/home-page/featured-poll-card/featured-card-poll.component';
import { environment } from '@tytapp/environment';
import { HomeCard, HomeCardComponentTypeRegistration, HomeCardsService } from '@tytapp/app/pages/home-page/home-cards.service';
import { skip } from 'rxjs/operators';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss']
})
export class HomeComponent extends PageComponent implements OnInit {
    private userService = inject(UserService);
    private billing = inject(BillingService);
    private pageApiService = inject(PageApiService);
    private opengraph = inject(OpenGraph);
    private appConfig = inject(AppConfig);
    private hostApi = inject(HostApi);
    private homeCards = inject(HomeCardsService);

    get pageComponentId() {
        return 'home';
    }

    get isDevelopment() {
        return this.appConfig.environment == 'staging'
            || this.appConfig.environment == 'development'
            ;
    }

    ready: boolean = false;
    user: ApiUser;
    poll: ApiPoll;
    petition: ApiPoll;
    articles: ApiArticle[];
    carouselSlides: ApiCarouselSlide[];
    streams: ApiLiveStream[];

    get isServerSide() {
        return isServerSide();
    }

    get audienceType() {
        let value = this.userService.audienceType;
        if (value === 'registered') {
            // HACK
            // Treat registered users as "visitor" role for now for the purposes of the
            // home page carousel. This must be removed in order to have unique carousel slides
            // for registered users vs unregistered visitors
            // HACK
            value = 'visitor';
        }

        return value;
    }

    isAudience(intended: string[]) {
        if (intended ?? null === null)
            return true;
        if (intended.includes(""))
            return true; // BUG in TYT Platform 9/20/2023 when user selects "All"

        return intended.includes(this.audienceType);
    }

    cards: HomeCard[] = undefined;
    follows: ApiTopic[] = undefined;
    followedShows: ApiFollowedShow[] = undefined;

    get primaryCards() {
        return this.cards?.filter(x => this.cardIsPrimary(x)) ?? [];
    }

    get secondaryCards() {
        return this.cards?.filter(x => !this.cardIsPrimary(x)) ?? [];
    }

    signupC2AEnabled = true;
    get showSignupC2A() {
        return this.signupC2AEnabled && !this.user && this.userService.isReady;
    }

    @HostBinding('class.animate-in')
    animateIn = false;

    async buildRows() {
        await this.shell.waitForFeatures();

        let homePageArrived = false;
        let mustSeeCard: ApiHomeRow & HomeCard = { type: 'must_see', topic_items: [], placeholder: true, primary: true };
        let petitionCard: PetitionHomeCard = { type: 'petition', petition: undefined, placeholder: !homePageArrived, primary: true };
        let pollCard: PollHomeCard = { type: 'poll', poll: undefined, placeholder: !homePageArrived, primary: false };
        let topicCards: (ApiHomeRow & HomeCard)[] = [
            { type: 'topic', topic_items: [], placeholder: true, primary: true },
            { type: 'topic', topic_items: [], placeholder: true, primary: true },
            { type: 'topic', topic_items: [], placeholder: true, primary: true },
            { type: 'topic', topic_items: [], placeholder: true, primary: true },
        ];

        this.cards = [
            { type: 'featured_members', primary: true },
            { type: 'live_schedule', primary: false },
            { type: 'continue_watching', primary: true },
            { type: 'signup_c2a', primary: true },
            mustSeeCard,
            { type: 'newsletter', primary: false },
            pollCard,
            petitionCard,
            { type: 'plus', primary: true },
            { type: 'announcements', primary: false },
            ...(
                await this.shell.hasFeature('apps.web.operation_hope') ?
                    [
                        { type: 'latest_operation_hope_posts', primary: true },
                        { type: 'top_operation_hope_posts', primary: true },
                    ]
                    : [
                        { type: 'latest_community_forum_posts', primary: true },
                        { type: 'top_community_forum_posts', primary: true },
                    ]
            ),
            { type: 'tune_in', primary: false },
            ...topicCards
        ];

        this.ready = true;

        if (isClientSide())
            await sleep(1);
        this.animateIn = true;

        try {
            let homePage: ApiHomePage;

            if (!this.userService.user) {
                homePage = await this.anonymousHomeCache.fetch('default', async () => await this.pageApiService.home());
            } else {
                homePage = await this.homeCache.fetch(`user_${this.userService.user.id}`, async () => await this.pageApiService.home());
            }

            pollCard.poll = homePage.poll;
            petitionCard.petition = homePage.petition;
            this.carouselSlides = homePage.carousel_slides;

            let mustSeeContent = homePage.rows.find(x => x.type === 'must_see');
            if (mustSeeContent) {
                mustSeeCard.topic_items = mustSeeContent.topic_items;
                mustSeeCard.placeholder = false;
            } else {
                this.logger.error(
                    `Error: No must_see row found in home page! The card will be removed. `
                    + `This will cause layout shift!`
                );
                this.cards = this.cards.filter(x => x.type !== 'must_see');
            }

            if (homePage?.rows) {
                for (let row of homePage.rows) {
                    if (row.type !== 'topic')
                        continue;
                    let topicCard = topicCards.shift();
                    if (!topicCard) {
                        this.cards.push(row);
                    } else {
                        Object.assign(topicCard, row);
                        topicCard.placeholder = false;
                    }
                }

                if (topicCards.length > 0) {
                    this.logger.warning(
                        `Error: ${topicCards.length} placeholder topic cards were unused! `
                        + `This will cause a bit of layout shift as they are removed.`
                    );
                    this.cards = this.cards.filter(x => !topicCards.some(y => x === y));
                    topicCards = [];
                }
            }

        } catch (e) {
            this.logger.error(`Failed to load home page:`);
            this.logger.error(e);
            this.failedToLoad = true;
            return;
        }
    }

    failedToLoad = false;

    anonymousHomeCache = Cache.shared<ApiHomePage>('home-anonymous', 1000 * 60 * 10, 1);
    homeCache = Cache.shared<ApiHomePage>('home', 1000 * 60 * 5, 100);
    get webBillingEnabled() { return this.hostApi.capabilities.includes('web_billing:membership'); }
    get platformBillingEnabled() { return this.hostApi.capabilities.includes('platform_billing:membership'); }

    get billingEnabled() {
        return this.webBillingEnabled || this.platformBillingEnabled;
    }

    async init() {
        this.pageTitle = 'Home';

        this.head.setCanonicalLink('/');
        this.opengraph.setLdJson({
            "@context": "http://schema.org",
            "@type": "Organization",
            "name": "The Young Turks",
            "url": "https://tyt.com",
            "sameAs": [
                "https://www.facebook.com/TheYoungTurks",
                "https://twitter.com/TheYoungTurks",
                "http://www.youtube.com/theyoungturks",
                "https://www.linkedin.com/company/the-young-turks",
                "http://tytnetwork.tumblr.com/",
                "http://www.pinterest.com/tytnetwork",
                "http://instagram.com/tytnetwork",
                "https://plus.google.com/+TheYoungTurks"]
        });

        this.subscribe(this.userService.onboardingChanged, onboarding => this.onboarding = onboarding);
        this.subscribe(this.billing.entitlementChanged, entitled => this.entitled = entitled);

        let firstUserChange = true;

        let userId: string = null;
        this.subscribe(this.userService.userChanged, async user => {
            if (userId === user?.uuid)
                return;

            userId = user?.uuid;
            this.user = user;
            await this.fetchHomePage();


            if (firstUserChange) {
                // When user becomes entitled or not entitled, we should ensure we get a fresh home page from the server
                // so that the contents of the home page match the new entitlement status. For instance, non-entitled users
                // typically see clips on the home page, whereas entitled users typically see episodes.
                this.subscribe(this.billing.entitlementChanged.pipe(skip(1)), async () => {
                    this.pageApiService.bust(`home_${this.userService.identityKey}`);
                    await this.fetchHomePage();
                });
                firstUserChange = false;
            }
        });
    }

    private async fetchHomePage() {
        this.buildRows();
    }

    onboarding = false;
    entitled = false;

    setUserSource(source: string) {
        this.userService.source = source;
    }

    cardIsPrimary(card: HomeCard) {
        return card.primary ?? false;
    }

    enableDevTools = environment.showDevTools;

    showInfo = false;
    editCards = false;
    cardJson: string;
    forceVisible = false;

    startEditingCards() {
        this.editCards = true;
        this.cardJson = JSON.stringify(this.cards, undefined, 2);
    }

    saveEditedCards() {
        this.cards = JSON.parse(this.cardJson);
        this.editCards = false;
    }

    cancelEditingCards() {
        this.editCards = false;
    }

    get cardTypes() {
        return this.homeCards.getTypes();
    }

    addCard(reg: HomeCardComponentTypeRegistration) {
        this.cards.unshift({ type: reg.id });
        this.cardJson = JSON.stringify(this.cards);
    }

    removeCard(card: HomeCard) {
        this.cards = this.cards.filter(x => x !== card);
        this.cardJson = JSON.stringify(this.cards);
    }
}
